import { z } from 'zod/v4';
import { PlanResourceSchema } from './plan-resource.js';

export const PlanDetailParamsSchema = z.object({
  id: z.string().min(1, 'CAP ID is required'),
});

export type PlanDetailParams = z.infer<typeof PlanDetailParamsSchema>;

/**
 * One plan on the `GET /api/capa/caps/:id` wire: every field of
 * `PlanResourceSchema` plus the caller's roles on it.
 *
 * `userRoles` / `companyRoles` are caller-relative — two companies looking at
 * the same plan see different values — and both dashboards gate their edit
 * controls on them, so they are contract, not convenience. They come from
 * `deriveActionRoles`, reading the link rows this query already returns; the
 * read this replaced ran a second access-control query for them.
 *
 * Wire-format notes (do not "fix" — both dashboards read this shape):
 * - Dates are `Date` objects here and ISO strings on the wire; the route's
 *   serializer converts them.
 * - `isArchived` is the raw `0`/`1` and means "archived by *your* company":
 *   per-viewer membership in the action-archive table, never the legacy
 *   `is_archived` column.
 * - `status`, `startedAt` and `endedAt` are child rollups, and `findings[].status`
 *   is a rollup of its own. None of them is the stored column.
 * - `internalCompanyCode` and `notes` stay optional: they are selected by the
 *   list read only, so this response omits them.
 * - This is a plain object schema, so an undeclared key is stripped rather than
 *   passed through. Adding a column to the query means declaring it here; the
 *   integration test asserts the key set both ways.
 */
export const PlanDetailSchema = PlanResourceSchema.extend({
  userRoles: z.array(z.string()),
  companyRoles: z.array(z.string()),
  // Fields the resource schema leaves optional because the *list* read does not
  // select them. The detail read always does — its SQL coalesces each to an
  // empty array, and builds `owner`/`source` unconditionally — so they are
  // required here, and the key-set integration assertion proves it against a
  // real response.
  owner: PlanResourceSchema.shape.owner.unwrap(),
  source: PlanResourceSchema.shape.source.unwrap(),
  tags: PlanResourceSchema.shape.tags.unwrap(),
  companies: PlanResourceSchema.shape.companies.unwrap(),
});

export type PlanDetail = z.infer<typeof PlanDetailSchema>;
